import React, { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Button,
  Chip,
  Drawer,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import ExpandMoreOutlined from "@mui/icons-material/ExpandMoreOutlined";
import DialpadOutlined from "@mui/icons-material/DialpadOutlined";
import BuildOutlined from "@mui/icons-material/BuildOutlined";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineOutlined from "@mui/icons-material/PersonOutlineOutlined";
import VerifiedOutlined from "@mui/icons-material/VerifiedOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import {
  outcomeInfo,
  sentimentInfo,
  formatDuration,
  formatFecha,
  transferResultInfo,
  SATISFACTION_LEVELS,
} from "../../helpers/callFormatting";

const PAPER_WIDTH = 400;

const useStyles = makeStyles()((theme) => ({
  header: {
    flexShrink: 0,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  title: {
    fontWeight: 600,
    fontSize: "1rem",
    lineHeight: 1.3,
  },
  subtitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: 2,
  },
  subtitle: {
    flexShrink: 0,
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  callIdRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    flex: "0 1 auto",
    minWidth: 0,
    fontSize: "0.7rem",
    color: theme.palette.text.disabled,
    fontFamily: "ui-monospace, Consolas, monospace",
    overflow: "hidden",
  },
  callIdText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  copyIcon: {
    fontSize: 12,
    flexShrink: 0,
    cursor: "pointer",
    "&:hover": { color: theme.palette.text.secondary },
  },
  fixedBody: {
    flexShrink: 0,
    padding: theme.spacing(1.5, 2.5, 0),
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(1, 2.5, 2),
    ...theme.scrollbarStyles,
  },
  block: {
    paddingBottom: theme.spacing(1.25),
    marginBottom: theme.spacing(1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  infoLine: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    fontSize: "0.82rem",
    "& + &": {
      marginTop: theme.spacing(0.5),
    },
  },
  infoLabel: {
    color: theme.palette.text.secondary,
  },
  audio: {
    display: "block",
    width: "100%",
    height: 34,
  },
  summaryToggle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.75),
    cursor: "pointer",
    userSelect: "none",
  },
  summaryLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  chevron: {
    fontSize: 18,
    color: theme.palette.text.secondary,
    flexShrink: 0,
    transition: "transform 0.15s ease",
  },
  chevronOpen: {
    transform: "rotate(180deg)",
  },
  summaryText: {
    marginTop: theme.spacing(0.75),
    padding: theme.spacing(1, 1.25),
    borderRadius: 10,
    background: theme.palette.mode === "light" ? "#f5f4f1" : "#242320",
    fontSize: "0.82rem",
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
  },
  sectionTitle: {
    marginBottom: theme.spacing(0.75),
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: theme.palette.text.disabled,
  },
  transcript: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.85),
  },
  turnBlock: {
    width: "100%",
    boxSizing: "border-box",
  },
  turnHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: theme.spacing(1),
    padding: theme.spacing(0, 0.2),
    marginBottom: 2,
  },
  turnRole: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.7rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  turnBubble: {
    width: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(0.75, 1.1),
    borderRadius: 8,
    fontSize: "0.86rem",
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  turnBubbleUser: {
    background: theme.palette.mode === "light" ? "#e4e8fb" : "#262b52",
  },
  turnBubbleAssistant: {
    background: theme.palette.mode === "light" ? "#f0efec" : "#2a2a28",
  },
  turnTime: {
    flexShrink: 0,
    fontSize: "0.65rem",
    color: theme.palette.text.disabled,
    marginTop: 3,
    padding: theme.spacing(0, 0.5),
  },
  systemLine: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    width: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(0.15, 0.9),
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
  },
  systemLineText: {
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  toolCard: {
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
  },
  toolCardCollapsed: {
    padding: theme.spacing(0.15, 0.9),
  },
  toolCardOpen: {
    padding: theme.spacing(0.4, 0.9, 0.6),
    borderRadius: 6,
    background: theme.palette.mode === "light" ? "#eef8f1" : "#1b2b20",
    borderLeft: "3px solid #2fae60",
  },
  toolCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    minWidth: 0,
  },
  toolCardLabel: {
    flexShrink: 0,
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: "#2fae60",
  },
  toolCardInline: {
    flex: "0 1 auto",
    minWidth: 0,
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  toolCardChevron: {
    fontSize: 14,
    flexShrink: 0,
    color: "#2fae60",
    transition: "transform 0.15s ease",
  },
  toolCardBody: {
    marginTop: theme.spacing(0.5),
    fontSize: "0.78rem",
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  toolCardCode: {
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1),
    borderRadius: 6,
    background: theme.palette.mode === "light" ? "#eceae5" : "#161513",
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: "0.72rem",
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  toolCardResult: {
    marginTop: theme.spacing(0.5),
    fontSize: "0.78rem",
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  toolCardTime: {
    display: "block",
    marginTop: theme.spacing(0.5),
    fontSize: "0.62rem",
    color: theme.palette.text.disabled,
  },
  empty: {
    padding: theme.spacing(3, 0),
    textAlign: "center",
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
  },
  tabs: {
    flexShrink: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 38,
    "& .MuiTab-root": {
      minHeight: 38,
      fontSize: "0.78rem",
      textTransform: "none",
    },
  },
  logBar: {
    flexShrink: 0,
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(1, 2.5, 0),
  },
  analysisRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  analysisTitle: {
    marginBottom: 0,
    marginRight: theme.spacing(0.5),
  },
  auditBox: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
  },
  auditRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    fontSize: "0.82rem",
  },
  auditActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  auditMeta: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
  },
  logText: {
    margin: 0,
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: "0.72rem",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: theme.palette.text.secondary,
  },
}));

const formatAtSeconds = (segundos) => {
  const min = Math.floor(segundos / 60);
  const seg = segundos % 60;
  return `${min}:${String(seg).padStart(2, "0")}`;
};

const ROLE_LABEL = {
  user: "CLIENTE",
  assistant: "IA",
  dtmf: "DTMF",
  tool: "TOOL",
};

const groupTurns = (turns) => {
  const agrupados = [];

  turns.forEach((turn) => {
    if (turn.role === "dtmf") {
      const anterior = agrupados[agrupados.length - 1];
      if (anterior && anterior.role === "dtmf") {
        anterior.text += turn.text;
        return;
      }
      agrupados.push({ ...turn });
      return;
    }
    agrupados.push(turn);
  });

  return agrupados;
};

const buildLogText = (detail) => {
  const lineas = [
    `Fecha: ${formatFecha(detail.createdAt)}`,
    `Llamada: ${detail.callerNumber || "—"} → ${detail.toNumber || "—"}`,
    `Duración: ${formatDuration(detail.durationSeconds)}`,
    `Conexión: ${detail.connectionName || "—"}`,
    `Resultado: ${detail.outcome}${detail.transferDialStatus ? ` (${detail.transferDialStatus})` : ""}`,
    ...(detail.outcome === "transferred"
      ? [
          `Atendió: ${detail.transferAnsweredExtension ? `Ext. ${detail.transferAnsweredExtension}${detail.transferAnsweredName ? ` · ${detail.transferAnsweredName}` : ""}` : "—"}${detail.transferChain ? ` (${detail.transferChain})` : ""}`,
        ]
      : []),
    `Call ID: ${detail.callId || "—"}`,
    "",
  ];

  const turns = groupTurns(detail.turns);

  if (!turns.length) {
    lineas.push("(sin transcripción)");
  } else {
    turns.forEach((turn) => {
      const etiqueta = ROLE_LABEL[turn.role] || turn.role.toUpperCase();
      lineas.push(`[${formatAtSeconds(turn.atSeconds)}] ${etiqueta}: ${turn.text}`);
    });
  }

  return lineas.join("\n");
};

const yesNo = (value) => (value === null || value === undefined ? "—" : value ? "Sí" : "No");

const CallDetailModal = ({ open, callId, onClose, onAudited }) => {
  const { classes } = useStyles();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [expandedTools, setExpandedTools] = useState(() => new Set());
  const [tab, setTab] = useState("detalle");
  const [audit, setAudit] = useState({ successful: null, sentiment: "", comment: "" });
  const [savingAudit, setSavingAudit] = useState(false);

  const applyDetail = (data) => {
    setDetail(data);
    setAudit({
      successful: data.auditSuccessful ?? null,
      sentiment: data.auditSentiment || "",
      comment: data.auditComment || "",
    });
  };

  useEffect(() => {
    if (!open || !callId) return;

    setDetail(null);
    setLoading(true);
    setSummaryExpanded(false);
    setExpandedTools(new Set());
    setTab("detalle");

    (async () => {
      try {
        const { data } = await api.get(`/calls/history/${callId}`);
        applyDetail(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, callId]);

  const info = detail ? outcomeInfo(detail.outcome) : null;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const audited = Boolean(detail?.auditedAt);
  const hasAnalysis =
    detail &&
    (audited || detail.analysisSummary || detail.analysisSuccessful !== null || detail.analysisSentiment);
  const effectiveSuccessful = detail ? detail.auditSuccessful ?? detail.analysisSuccessful : null;
  const effectiveSentiment = detail ? detail.auditSentiment || detail.analysisSentiment : null;
  const auditDirty =
    detail &&
    (audit.successful !== (detail.auditSuccessful ?? null) ||
      audit.sentiment !== (detail.auditSentiment || "") ||
      audit.comment.trim() !== (detail.auditComment || ""));

  const saveAudit = async (values) => {
    setSavingAudit(true);
    try {
      const { data } = await api.put(`/calls/history/${callId}/audit`, {
        successful: values.successful,
        sentiment: values.sentiment || null,
        comment: values.comment,
      });
      applyDetail(data);
      onAudited?.(data);
      toast.success("Auditoría guardada.");
    } catch (err) {
      toastError(err);
    }
    setSavingAudit(false);
  };

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success("Copiado.");
    } catch {}
  };

  const toggleTool = (i) => {
    setExpandedTools((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1400 }}
      slotProps={{
        paper: {
          sx: {
            width: PAPER_WIDTH,
            maxWidth: "100vw",
            backgroundColor: (theme) => theme.palette.background.paper,
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <div className={classes.header}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Typography className={classes.title}>
            {detail ? formatFecha(detail.createdAt) : "Llamada"}
          </Typography>
          {detail && (
            <div className={classes.subtitleRow}>
              <Typography className={classes.subtitle}>Llamada telefónica</Typography>
              {detail.callId && (
                <div className={classes.callIdRow}>
                  <span className={classes.callIdText}>{detail.callId}</span>
                  <ContentCopyOutlined className={classes.copyIcon} onClick={() => copiar(detail.callId)} />
                </div>
              )}
            </div>
          )}
        </div>
        <IconButton onClick={onClose} size="small">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </div>

      {!loading && detail && (
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="fullWidth"
        >
          <Tab value="detalle" label="Detalle" />
          <Tab value="log" label="Log" />
        </Tabs>
      )}

      {loading && (
        <div className={classes.scrollArea}>
          <Typography className={classes.empty}>Cargando…</Typography>
        </div>
      )}

      {!loading && detail && tab === "detalle" && (
        <>
          {detail.recordingUrl && (
            <div className={classes.fixedBody}>
              <div className={classes.block}>
                <audio className={classes.audio} controls src={`${backendUrl}${detail.recordingUrl}`} />
              </div>
            </div>
          )}

          <div className={classes.scrollArea}>
            <div className={classes.block}>
              <div className={classes.infoLine}>
                <span className={classes.infoLabel}>Llamada</span>
                <strong>{detail.callerNumber || "—"} → {detail.toNumber || "—"}</strong>
              </div>
              <div className={classes.infoLine}>
                <span className={classes.infoLabel}>Duración</span>
                <strong>{formatDuration(detail.durationSeconds)}</strong>
              </div>
              <div className={classes.infoLine}>
                <span className={classes.infoLabel}>Costo</span>
                <strong>${detail.estimatedCostUsd.toFixed(2)}</strong>
              </div>
              <div className={classes.infoLine}>
                <span className={classes.infoLabel}>Conexión</span>
                <strong>{detail.connectionName || "—"}</strong>
              </div>
              <div className={classes.infoLine}>
                <span className={classes.infoLabel}>Resultado</span>
                {detail.outcome === "transferred" ? (
                  <strong style={{ color: transferResultInfo(detail.transferDialStatus).color }}>
                    {transferResultInfo(detail.transferDialStatus).label}
                  </strong>
                ) : (
                  <strong style={{ color: info.color }}>{info.label}</strong>
                )}
              </div>
              {detail.outcome === "transferred" && (
                <div className={classes.infoLine}>
                  <span className={classes.infoLabel}>Atendió</span>
                  <strong title={detail.transferChain || undefined}>
                    {detail.transferAnsweredExtension
                      ? `Ext. ${detail.transferAnsweredExtension}${detail.transferAnsweredName ? ` · ${detail.transferAnsweredName}` : ""}`
                      : "—"}
                  </strong>
                </div>
              )}
            </div>

            {hasAnalysis && (
              <div className={classes.block}>
                <div className={classes.analysisRow}>
                  <Typography className={`${classes.sectionTitle} ${classes.analysisTitle}`}>Análisis</Typography>
                  {effectiveSuccessful !== null && effectiveSuccessful !== undefined && (
                    <Tooltip
                      title={
                        detail.auditSuccessful !== null
                          ? `Auditado por ${detail.auditedByName || "—"} · La IA dijo: ${yesNo(detail.analysisSuccessful)}`
                          : "Según la IA"
                      }
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        color={effectiveSuccessful ? "success" : "error"}
                        icon={detail.auditSuccessful !== null ? <VerifiedOutlined /> : undefined}
                        label={`Resuelto: ${yesNo(effectiveSuccessful)}`}
                      />
                    </Tooltip>
                  )}
                  {sentimentInfo(effectiveSentiment) && (
                    <Tooltip
                      title={
                        detail.auditSentiment
                          ? `Auditado por ${detail.auditedByName || "—"} · La IA dijo: ${sentimentInfo(detail.analysisSentiment)?.label || "—"}`
                          : "Según la IA"
                      }
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        color={sentimentInfo(effectiveSentiment).chipColor}
                        icon={detail.auditSentiment ? <VerifiedOutlined /> : undefined}
                        label={sentimentInfo(effectiveSentiment).label}
                      />
                    </Tooltip>
                  )}
                </div>
                {detail.analysisSummary && (
                  <>
                    <div
                      className={classes.summaryToggle}
                      onClick={() => setSummaryExpanded((v) => !v)}
                    >
                      <span className={classes.summaryLabel}>Resumen</span>
                      <ExpandMoreOutlined
                        className={`${classes.chevron} ${summaryExpanded ? classes.chevronOpen : ""}`}
                      />
                    </div>
                    {summaryExpanded && (
                      <Typography className={classes.summaryText}>
                        {detail.analysisSummary}
                      </Typography>
                    )}
                  </>
                )}
              </div>
            )}

            <Typography className={classes.sectionTitle}>Transcripción</Typography>

            {detail.turns.length === 0 ? (
              <Typography className={classes.empty}>Sin transcripción para esta llamada.</Typography>
            ) : (
              <div className={classes.transcript}>
                {groupTurns(detail.turns).map((turn, i) => {
                  if (turn.role === "dtmf") {
                    return (
                      <div key={i} className={classes.systemLine} title={`Marcó por teclado: ${turn.text}`}>
                        <DialpadOutlined style={{ fontSize: 13, flexShrink: 0 }} />
                        <span className={classes.systemLineText}>Marcó: {turn.text}</span>
                        <span className={classes.turnTime} style={{ marginTop: 0 }}>
                          {formatAtSeconds(turn.atSeconds)}
                        </span>
                      </div>
                    );
                  }

                  if (turn.role === "tool") {
                    const abierto = expandedTools.has(i);
                    const esConfig = turn.text.startsWith("config_activa →");
                    const esRuido = turn.text.startsWith("ruido_descartado →");
                    const esOmitida = turn.text.startsWith("respuesta_omitida →");
                    const esGenerica = !esConfig && !esRuido && !esOmitida;
                    const label = esConfig
                      ? "Configuración"
                      : esRuido
                      ? "Ruido ignorado"
                      : esOmitida
                      ? "Sin respuesta aparte"
                      : "Herramienta";

                    const flechaIdx = esGenerica ? turn.text.indexOf(" → ") : -1;
                    const llamado = flechaIdx >= 0 ? turn.text.slice(0, flechaIdx) : null;
                    const resultado = flechaIdx >= 0 ? turn.text.slice(flechaIdx + 3) : null;
                    const nombreHerramienta = llamado ? llamado.split(/[\s{]/)[0] : null;

                    return (
                      <div
                        key={i}
                        className={`${classes.toolCard} ${abierto ? classes.toolCardOpen : classes.toolCardCollapsed}`}
                        onClick={() => toggleTool(i)}
                      >
                        <div className={classes.toolCardHeader}>
                          <BuildOutlined style={{ fontSize: 12, flexShrink: 0, color: "#2fae60" }} />
                          <span className={classes.toolCardLabel}>{label}</span>
                          {!abierto && (
                            <span className={classes.toolCardInline}>{nombreHerramienta || turn.text}</span>
                          )}
                          <ExpandMoreOutlined
                            className={`${classes.toolCardChevron} ${abierto ? classes.chevronOpen : ""}`}
                          />
                        </div>
                        {abierto && (
                          <>
                            {llamado !== null ? (
                              <>
                                <div className={classes.toolCardCode}>{llamado}</div>
                                {resultado && <div className={classes.toolCardResult}>{resultado}</div>}
                              </>
                            ) : (
                              <div className={classes.toolCardBody}>{turn.text}</div>
                            )}
                            <span className={classes.toolCardTime}>{formatAtSeconds(turn.atSeconds)}</span>
                          </>
                        )}
                      </div>
                    );
                  }

                  const esUsuario = turn.role === "user";
                  return (
                    <div key={i} className={classes.turnBlock}>
                      <div className={classes.turnHeader}>
                        <span className={classes.turnRole}>
                          {esUsuario ? (
                            <PersonOutlineOutlined style={{ fontSize: 13 }} />
                          ) : (
                            <SmartToyOutlined style={{ fontSize: 13 }} />
                          )}
                          {esUsuario ? "Cliente" : "Agente"}
                        </span>
                        <span className={classes.turnTime} style={{ marginTop: 0, padding: 0 }}>
                          {formatAtSeconds(turn.atSeconds)}
                        </span>
                      </div>
                      <div className={`${classes.turnBubble} ${esUsuario ? classes.turnBubbleUser : classes.turnBubbleAssistant}`}>
                        {turn.text}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className={classes.auditBox}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                Auditoría
              </Typography>
              <div className={classes.auditRow}>
                <span className={classes.infoLabel}>¿Se resolvió?</span>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={audit.successful}
                  onChange={(e, value) => setAudit((prev) => ({ ...prev, successful: value }))}
                >
                  <ToggleButton value={true} sx={{ px: 1.5, py: 0.25 }}>
                    Sí
                  </ToggleButton>
                  <ToggleButton value={false} sx={{ px: 1.5, py: 0.25 }}>
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>
              <TextField
                select
                size="small"
                label="Sentimiento real del cliente"
                value={audit.sentiment}
                onChange={(e) => setAudit((prev) => ({ ...prev, sentiment: e.target.value }))}
                fullWidth
                slotProps={{
                  inputLabel: { shrink: true },
                  select: { displayEmpty: true, MenuProps: { sx: { zIndex: 1500 } } },
                }}
              >
                <MenuItem value="">
                  <em>Sin auditar</em>
                </MenuItem>
                {SATISFACTION_LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {sentimentInfo(level)?.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Comentario"
                placeholder="Qué estuvo bien o mal en la respuesta de la IA"
                value={audit.comment}
                onChange={(e) => setAudit((prev) => ({ ...prev, comment: e.target.value }))}
                multiline
                minRows={3}
                fullWidth
              />
              {audited && (
                <Typography className={classes.auditMeta}>
                  Auditado por {detail.auditedByName || "—"} · {formatFecha(detail.auditedAt)}
                </Typography>
              )}
              <div className={classes.auditActions}>
                {audited && (
                  <Button
                    size="small"
                    color="inherit"
                    disabled={savingAudit}
                    onClick={() => saveAudit({ successful: null, sentiment: "", comment: "" })}
                  >
                    Quitar auditoría
                  </Button>
                )}
                <Button
                  size="small"
                  variant="contained"
                  disabled={savingAudit || !auditDirty}
                  onClick={() => saveAudit(audit)}
                >
                  Guardar auditoría
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {!loading && detail && tab === "log" && (
        <>
          <div className={classes.logBar}>
            <Button
              size="small"
              startIcon={<ContentCopyOutlined fontSize="small" />}
              onClick={() => copiar(buildLogText(detail))}
            >
              Copiar todo
            </Button>
          </div>
          <div className={classes.scrollArea}>
            <pre className={classes.logText}>{buildLogText(detail)}</pre>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default CallDetailModal;
